<script lang="ts">
	// The reports section, as "Home Visibility" draws it, holding only what
	// the Reports page (UC-19) shows: sales, paid orders, the average order,
	// the busiest hour, items, cash and card, and voids. No comparison with an
	// earlier period, which the report does not make. The numbers are examples.

	import Card from '../components/Card.svelte';
	import Eyebrow from '../components/Eyebrow.svelte';
	import Meter from '../components/Meter.svelte';
	import Row from '../components/Row.svelte';
	import Segmented from '../components/Segmented.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { reports, type Range } from './copy';
	import Rich from './Rich.svelte';

	let range = $state<Range>('week');

	const report = $derived(reports.byRange[range]);
	const options = reports.ranges.map((option) => ({
		...option,
		testId: `site-reports-${option.value}`,
	}));
</script>

<section id="reports" class="section">
	<div class="top">
		<div class="measure">
			<div class="kicker"><Eyebrow tone="ledger" text={reports.eyebrow} /></div>
			<h2 class="h2">{reports.title}</h2>
			<p class="copy">{reports.body}</p>
			<p class="caption example">{reports.example}</p>
		</div>
		<Segmented
			label={reports.rangeLabel}
			{options}
			value={range}
			onChange={(value) => (range = value as Range)}
			testId="site-reports-range"
		/>
	</div>

	<div class="figures">
		{#each report.figures as figure (figure.label)}
			<Card pad>
				<div class="eyebrow"><Eyebrow text={figure.label} /></div>
				<p class="figure value">{figure.value}</p>
				<p class="sub"><Rich line={figure.sub} /></p>
			</Card>
		{/each}
	</div>

	<div class="panels">
		<TablePanel
			title={reports.itemsTitle}
			note={report.note}
			columns={reports.itemColumns}
			rowMin="420px"
			testId="site-reports-items"
		>
			{#each report.items as item, index (item.name)}
				<Row>
					<span class="name">{item.name}</span>
					<span class="figure cell end">{item.qty}</span>
					<span class="meter"><Meter value={item.share} dim={index > 0} label={item.name} /></span>
					<span class="figure cell end strong">{item.revenue}</span>
				</Row>
			{/each}
		</TablePanel>

		<Card title={reports.payTitle} note={report.note}>
			<div class="pay">
				{#each report.pay as row (row.label)}
					<div>
						<div class="pay-head">
							<span class="pay-label">{row.label}</span>
							<span class="figure pay-value">{row.display}</span>
						</div>
						<Meter value={row.value} tone={row.tone} label={row.label} />
					</div>
				{/each}
				<p class="pay-note">{reports.payNote}</p>
			</div>
		</Card>
	</div>
</section>

<style>
	.top {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-7);
		align-items: flex-end;
		justify-content: space-between;
		margin-block-end: var(--space-10);
	}

	.example {
		margin-block-start: var(--space-4);
	}

	.figures {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
		gap: var(--grid-gap);
		margin-block-end: var(--grid-gap);
	}

	.eyebrow {
		margin-block-end: var(--space-3);
	}

	/* .figure runs left to right, so its end is the card's right edge, where
	   the design sets every figure. */
	.value {
		margin: 0;
		text-align: end;
		font-size: var(--text-stat);
		font-weight: var(--weight-bold);
		line-height: var(--leading-tight);
	}

	.sub {
		margin: var(--space-3) 0 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.panels {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
		gap: var(--grid-gap);
		align-items: start;
	}

	.name {
		min-inline-size: 0;
		font-size: var(--text-2xs);
		font-weight: var(--weight-semibold);
	}

	.cell {
		min-inline-size: 0;
		font-size: var(--text-2xs);
	}

	.end {
		text-align: end;
	}

	.strong {
		font-weight: var(--weight-bold);
	}

	.meter {
		display: block;
		min-inline-size: 0;
	}

	.pay {
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		padding: var(--space-9);
	}

	.pay-head {
		display: flex;
		justify-content: space-between;
		gap: var(--space-4);
		margin-block-end: var(--space-3);
		font-size: var(--text-2xs);
	}

	.pay-label,
	.pay-value {
		font-weight: var(--weight-semibold);
	}

	.pay-note {
		margin: 0;
		padding-block-start: var(--space-6);
		border-block-start: var(--border-width) solid var(--border-inset);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}
</style>
